import React, { useState, createRef } from 'react'
import {
  createStyles,
  Fade,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core'
import Button from '../Button/Button'
import Commitment from '../Commitment/Commitment'
import Loader from '../Loader/Loader'
import TextField from '../TextField/TextField'
import clsx from 'clsx'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import ReCAPTCHA from 'react-google-recaptcha'
import { disableRegister, isLoginSSO, isSSO } from '../../utils/constants'
import SSOLink from './SSOLink'
import CMSText, { prismicText } from '../Global/CMSText'
import { useRouter } from 'next/router'

const recaptchaKey = process.env.RECAPTCHA_SITE_KEY

const useStyles = makeStyles((theme) =>
  createStyles({
    loginForm: {
      borderRadius: theme.shape.borderRadius,
      background: theme.palette.common.white,
      padding: theme.spacing(2),
      maxWidth: '100%',
      margin: theme.spacing(0, 2),
    },
    loginFormCard: {
      padding: theme.spacing(3, 2, 2),
      [theme.breakpoints.up('sm')]: {
        maxWidth: 400,
        margin: 'auto',
      },
    },
    loginFormCardFunnel: {
      [theme.breakpoints.up('sm')]: {
        maxWidth: 450,
        margin: 'auto',
      },
    },
    alignCenter: {
      textAlign: 'center',
    },
    form: {
      maxWidth: 300,
      margin: 'auto',
    },
    link: {
      cursor: 'pointer',
      //textDecoration: 'underline',
      marginBottom: theme.spacing(2),
    },
    forgot: {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    alignLeft: {
      display: 'flex',
      justifyContent: 'flex-end',
      width: '100%',
    },
    marginBottom: {
      marginBottom: theme.spacing(2),
    },
    hide: {
      opacity: 0,
      transition: theme.transitions.create('opacity'),
    },
    show: {
      opacity: 1,
      transition: theme.transitions.create('opacity'),
    },
    recaptcha: {
      display: 'flex',
      marginBottom: theme.spacing(2),
      justifyContent: 'center',
    },
  })
)

type Tprops = {
  hasRegistered: any
  loginError: boolean
  text: any
  switchForm: any
  t?: any
  isFunnel: boolean
  isLoading?: boolean
  errorAccountDisabled?: boolean
  submitLogin: any
  takeBackTitle?: any
  isTakeBack?: boolean
}
const ssoRedirectUrl = (locale: string, onFunnel = false) => {
  if (!process.env.SSO_REDIRECT_URL) {
    return undefined
  }

  const baseUrl = process.env.SSO_REDIRECT_URL

  if (locale === 'fr') {
    return onFunnel ? `${baseUrl}shipping` : baseUrl
  }

  return onFunnel ? `${baseUrl}${locale}/shipping` : `${baseUrl}${locale}`
}

const ssoClientKey = (locale: string) => {
  if (locale === 'fr') {
    return process.env.SSO_CLIENT_KEY
  }

  const keys = {
    fr: process.env.SSO_CLIENT_KEY,
    de: process.env.SSO_CLIENT_KEY_DE,
    es: process.env.SSO_CLIENT_KEY_ES,
    it: process.env.SSO_CLIENT_KEY_IT,
  }
  return keys[locale.toLowerCase()] || process.env.SSO_CLIENT_KEY
}

const buildDecathlonSSOUrl = (
  clientKey: string,
  redirectUrl: string
): string => {
  return `${process.env.SSO_API_URL}/connect/oauth/authorize?client_id=${clientKey}&response_type=code&redirect_uri=${redirectUrl}&state=decathlon&scope=profile email gender birthdate address shipping_address phone`
}

const buildCulturaSSOUrl = (redirectUrl: string): string => {
  return `${process.env.SSO_API_URL}authorize?client_id=${process.env.SSO_CLIENT_KEY}&response_type=code&scope=profile offline_access openid email address phone&redirect_uri=${redirectUrl}`
}

const LoginForm = ({
  hasRegistered,
  loginError,
  switchForm,
  errorAccountDisabled,
  isLoading,
  submitLogin,
  t,
  isFunnel,
  text,
  takeBackTitle,
  isTakeBack = false,
}: Tprops) => {
  const classes = useStyles()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const { trackEvent } = useMatomo()
  const [captchaOK, setCaptchaOK] = useState(!recaptchaKey ? true : false)
  const recaptchaRef = createRef()
  const router = useRouter()
  const currentLocale = router.locale
  const isSubscription =
    process.env.STORE_TYPE === 'subscription' ||
    process.env.STORE_TYPE === 'classic_subscription'
  const commitmentOnLogin = text?.is_displayed_on_login
  const _ssoClientKey = ssoClientKey(currentLocale)
  const _ssoRedirectUrlOnFunnel = ssoRedirectUrl(currentLocale, true)
  const _ssoRedirectUrl = ssoRedirectUrl(currentLocale)

  const isSSOConfigured = !!(
    process.env.SSO_API_URL &&
    process.env.SSO_CLIENT_KEY &&
    process.env.SSO_REDIRECT_URL &&
    process.env.SSO_REDIRECT_URL_ON_FUNNEL
  )

  const onReCAPTCHAChange = (captchaCode) => {
    if (!captchaCode) {
      return
    } else {
      setCaptchaOK(true)
    }
  }

  const submitForm = (e) => {
    e.preventDefault()
    if (email !== '' && password !== '' && captchaOK) {
      const data = {
        email: email,
        password: password,
      }

      submitLogin(data)
    }
    trackEvent({
      category: 'User Information',
      action: `User click on login button`,
    })
  }

  return (
    <Fade in>
      <div
        className={
          !isFunnel
            ? `account-card login-card ${classes.loginForm} ${classes.loginFormCard}`
            : `account-form login-form ${classes.loginForm} ${classes.loginFormCardFunnel}`
        }
      >
        <div
          className={
            !isSubscription
              ? 'dad'
              : process.env.STORE_TYPE === 'subscription'
              ? 'sub'
              : 'csub'
          }
          data-testid={'guestUserBtn'}
        >
          {isFunnel && !isSubscription && text?.is_display_guest_link && (
            <div className={clsx(classes.alignCenter, classes.marginBottom)}>
              <Button
                data-testid="guestNext"
                variant={'contained'}
                color={'primary'}
                onClick={() => switchForm(2)}
                className={clsx(classes.link, 'login-form__button')}
              >
                {prismicText(text?.guest_link, t('texts:account:guest_link'))}
              </Button>
            </div>
          )}
        </div>
        <Typography
          variant={'h3'}
          align="center"
          className={'account__title'}
          gutterBottom
        >
          {prismicText(text?.title_login, t('texts:account:title_login'))}
        </Typography>

        <div>
          {hasRegistered && (
            <Typography
              variant={'body2'}
              color={'secondary'}
              align={'justify'}
              className={classes.marginBottom}
            >
              {prismicText(
                text?.successfull_register,
                t('texts:account:successfull_register')
              )}
            </Typography>
          )}
          <Typography
            variant={'body1'}
            className={clsx(
              'account__desc',
              classes.alignCenter,
              classes.marginBottom
            )}
          >
            {!isTakeBack && (
              <b>
                <CMSText
                  asText
                  data={text?.subtitle}
                  defaultText={t('texts:account:subtitle')}
                />
              </b>
            )}
            {isTakeBack && takeBackTitle && (
              <CMSText
                asText
                data={takeBackTitle}
                defaultText={t('texts:takeBack:login_text')}
              />
            )}
          </Typography>
          {isSSOConfigured && (
            <>
              {process.env.SSO_TYPE === 'decathlon' &&
                _ssoClientKey &&
                _ssoRedirectUrlOnFunnel &&
                _ssoRedirectUrl && (
                  <SSOLink
                    isLoading={isLoading}
                    defaultText={t('texts:account:sso_login:dkt')}
                    data={text?.dkt_login}
                    linkSSO={buildDecathlonSSOUrl(
                      _ssoClientKey,
                      isFunnel ? _ssoRedirectUrlOnFunnel : _ssoRedirectUrl
                    )}
                  />
                )}
              {process.env.SSO_TYPE === 'cultura' && (
                <SSOLink
                  isLoading={isLoading}
                  defaultText={t('texts:account:sso_login:cultura')}
                  data={text?.cultura_login}
                  linkSSO={buildCulturaSSOUrl(
                    isFunnel
                      ? process.env.SSO_REDIRECT_URL_ON_FUNNEL
                      : process.env.SSO_REDIRECT_URL
                  )}
                />
              )}
            </>
          )}
          {process.env.CIAM_ENABLED && (
            <SSOLink
              isLoading={isLoading}
              defaultText={t('texts:account:sso_login:ciam')}
              data={null}
              linkSSO={`${process.env.SSO_URL}?redirect_path=${
                isFunnel ? '/information' : '/account/informations'
              }`}
            />
          )}
          {(!isSSO || (isSSO && isLoginSSO === 'false')) && (
            <>
              <form onSubmit={submitForm}>
                <div
                  className={clsx(
                    'account__fields',
                    'account__hidden',
                    classes.form,
                    classes.marginBottom
                  )}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        label={prismicText(
                          text?.login_email,
                          t('texts:account:email')
                        )}
                        type="email"
                        name="email"
                        placeholder={t('texts:account:email')}
                        value={email}
                        required={true}
                        onChange={setEmail}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label={prismicText(
                          text?.login_password,
                          t('texts:account:password')
                        )}
                        type="password"
                        name="password"
                        placeholder={t('texts:account:password')}
                        value={password}
                        required={true}
                        onChange={setPassword}
                      />
                    </Grid>
                    <div className={classes.alignLeft}>
                      <Typography
                        variant={'body2'}
                        className={clsx('account__forgot', classes.forgot)}
                        onClick={() => switchForm(5)}
                      >
                        {prismicText(
                          text?.forget_password_link,
                          t('texts:account:forget_password_link')
                        )}
                      </Typography>
                    </div>
                  </Grid>
                </div>
                {recaptchaKey && (
                  <div className={classes.recaptcha}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={recaptchaKey}
                      onChange={onReCAPTCHAChange}
                    />
                  </div>
                )}
                {loginError && (
                  <Typography
                    variant={'body1'}
                    color={'error'}
                    className={clsx(
                      'account-login__error',
                      classes.alignCenter,
                      classes.marginBottom
                    )}
                  >
                    {prismicText(
                      text?.error_login,
                      t('texts:account:error_login')
                    )}
                  </Typography>
                )}
                {errorAccountDisabled && (
                  <Typography
                    variant={'body1'}
                    color={'error'}
                    className={clsx(
                      'account-login__error',
                      classes.alignCenter,
                      classes.marginBottom
                    )}
                  >
                    {prismicText(
                      text?.error_account_disabled,
                      t('texts:account:error_disabled')
                    )}
                  </Typography>
                )}
                <div
                  className={clsx(
                    classes.alignCenter,
                    classes.marginBottom,
                    'account-login__button'
                  )}
                >
                  <Button
                    type="submit"
                    variant={'contained'}
                    color={'secondary'}
                    disabled={
                      email === '' || password === '' || isLoading || !captchaOK
                    }
                    data-testid={`nextConnected`}
                  >
                    <span className={isLoading ? classes.hide : classes.show}>
                      {prismicText(
                        text?.login_button,
                        t('texts:account:login_button')
                      )}
                    </span>
                    {isLoading && <Loader />}
                  </Button>
                </div>
              </form>
              <div className={classes.alignCenter}>
                {disableRegister ? (
                  <Typography variant={'body2'}>
                    {t('texts:account:register_disable')}
                  </Typography>
                ) : (
                  <Typography
                    variant={'body2'}
                    onClick={() => switchForm(1)}
                    data-testid={'register_link'}
                    className={clsx('account__switch', classes.link)}
                  >
                    {prismicText(
                      text?.register_link,
                      t('texts:account:register_link')
                    )}
                  </Typography>
                )}
              </div>
            </>
          )}
          {commitmentOnLogin && !isTakeBack && (
            <div className={'commitment__area'}>
              <Commitment
                icon={
                  text?.protected_data_image?.url
                    ? text?.protected_data_image?.url
                    : null
                }
                title={prismicText(
                  text?.title_data,
                  t('texts:account:title_data')
                )}
                desc={
                  <CMSText
                    data={text?.text_data}
                    defaultText={t('texts:account:text_data')}
                  />
                }
                center={false}
              />
            </div>
          )}
        </div>
      </div>
    </Fade>
  )
}

export default LoginForm
